import React from 'react';
import { useTranslation } from 'react-i18next';
import FormattedCurrency from '../common/FormattedCurrency';
import JobListingType from './JobListingType';
import JobPublishedDate from './JobPublishedDate';
import JobTags from './JobTags';
import ProfileAvatar from '../common/ProfileAvatar';
import { Job } from '../../types/job';
import { QuoteJob } from '../../types/quote';

interface JobListCardHeaderProps {
  job: Job | QuoteJob;
  grandTotal?: number;
  projectMode?: boolean;
}

const JobListCardHeader: React.FC<JobListCardHeaderProps> = ({
  job,
  grandTotal,
  projectMode = false,
}) => {
  const { t, i18n } = useTranslation(['jobs', 'quotes', 'common']);

  const isJobPrivate = 'visibility' in job && job.visibility === 'private';
  const hasFreelancer = isJobPrivate && 'freelancer' in job && job.freelancer;

  return (
    <div>
      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[30%_70%]">
        <div className="md:pr-4">
          <h2 className="break-words text-xl font-bold text-secondary">{job.title}</h2>
        </div>

        <div className="xs:grid-cols-2 grid grid-cols-1 items-start gap-3 md:grid-cols-4">
          {!projectMode && (
            <div className="flex h-full items-start justify-start md:justify-center">
              <JobListingType job={job} t={t} className="text-xs" />
            </div>
          )}

          {grandTotal && (
            <div className="flex h-full flex-col items-start justify-center text-secondary md:items-center">
              <span className="text-xs">
                {t('grand_total', { ns: 'quotes', defaultValue: 'Grand total' })}
              </span>
              <FormattedCurrency value={grandTotal} className="text-sm text-secondary-light" />
            </div>
          )}

          <div className="flex h-full items-start justify-start md:justify-center">
            <JobPublishedDate job={job} t={t} language={i18n.language} className="text-xs" />
          </div>

          <div className="xs:col-span-2 flex h-full items-start gap-2 md:col-span-2">
            <JobTags job={job} t={t} projectMode={projectMode} className="text-xs" />

            {hasFreelancer && (
              <ProfileAvatar
                profile_id={job.freelancer?.id}
                first_name={job.freelancer?.first_name ?? ''}
                avatar_url={job.freelancer?.avatar_url}
                className="h-7 w-7 shrink-0 rounded-full object-cover"
              />
            )}
          </div>
        </div>
      </div>
      {job.description && (
        <div className="md:col-span-1 md:pr-4 border-t-2 border-t-light-secondary/30 pt-4 mt-4">
          <p className="break-words text-base text-secondary">{job.description}</p>
        </div>
      )}
    </div>
  );
};

export default JobListCardHeader;
